/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Project workspace activity visibility/share state and submit revision-aware commands.
 * @state-owner TanStack Query memory
 * @allowed-imports @vispo/api-client
 * @forbidden direct database access; client-side authorization decisions
 * @public-api activity access dialog
 * @side-effects authenticated visibility and complete-share replacement requests
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @tests workspace-access.test.ts
 */

import type { VispoApiClient } from '@vispo/api-client';
import { z } from 'zod';

export interface WorkspaceActivityAccess {
  activityId: string;
  organizationId: string;
  visibility: 'private' | 'organization';
  revision: number;
  teacherUserIds: string[];
}

export interface WorkspaceShareRecipient {
  userId: string;
  displayName: string;
}

export interface WorkspaceAccessState {
  activities: WorkspaceActivityAccess[];
  recipients: WorkspaceShareRecipient[];
}

const visibilitySchema = z.enum(['private', 'organization']);
const workspaceAccessSchema = z.object({
  organization_id: z.string().min(1),
  items: z.array(
    z.object({
      id: z.string().min(1),
      organization_id: z.string().min(1),
      visibility: visibilitySchema.nullable(),
      revision: z.number().int().positive().nullable(),
      teacher_user_ids: z.array(z.string().min(1)),
    }),
  ),
  share_recipients: z.array(
    z.object({ user_id: z.string().min(1), display_name: z.string().min(1) }),
  ),
});

const visibilityResponseSchema = z.object({
  visibility: visibilitySchema,
  revision: z.number().int().positive(),
});

const sharesResponseSchema = z.object({
  revision: z.number().int().positive(),
  teacher_user_ids: z.array(z.string().min(1)),
});

export async function workspaceAccess(
  apiClient: VispoApiClient,
  organizationId: string,
): Promise<WorkspaceAccessState> {
  const result = workspaceAccessSchema.parse(
    await apiClient.request<unknown>(
      `/api/v1/workspaces/${encodeURIComponent(organizationId)}/activities`,
    ),
  );
  return {
    activities: result.items.flatMap((item) =>
      item.visibility && item.revision
        ? [
            {
              activityId: item.id,
              organizationId: item.organization_id,
              visibility: item.visibility,
              revision: item.revision,
              teacherUserIds: item.teacher_user_ids,
            },
          ]
        : [],
    ),
    recipients: result.share_recipients.map((recipient) => ({
      userId: recipient.user_id,
      displayName: recipient.display_name,
    })),
  };
}

export async function updateWorkspaceActivityAccess(
  apiClient: VispoApiClient,
  current: WorkspaceActivityAccess,
  next: Pick<WorkspaceActivityAccess, 'visibility' | 'teacherUserIds'>,
): Promise<WorkspaceActivityAccess> {
  let revision = current.revision;
  let visibility = current.visibility;
  let teacherUserIds = current.teacherUserIds;
  if (next.visibility !== current.visibility) {
    const result = visibilityResponseSchema.parse(
      await apiClient.request<unknown>(
        `/api/v1/activities/${encodeURIComponent(current.activityId)}/visibility`,
        {
          method: 'PATCH',
          body: {
            expected_revision: revision,
            organization_id: current.organizationId,
            visibility: next.visibility,
          },
        },
      ),
    );
    revision = result.revision;
    visibility = result.visibility;
  }
  const sharesChanged =
    [...next.teacherUserIds].sort().join('\u0000') !==
    [...current.teacherUserIds].sort().join('\u0000');
  if (sharesChanged) {
    const result = sharesResponseSchema.parse(
      await apiClient.request<unknown>(
        `/api/v1/activities/${encodeURIComponent(current.activityId)}/teacher-shares`,
        {
          method: 'PUT',
          body: {
            expected_revision: revision,
            organization_id: current.organizationId,
            teacher_user_ids: next.teacherUserIds,
          },
        },
      ),
    );
    revision = result.revision;
    teacherUserIds = result.teacher_user_ids;
  }
  return { ...current, revision, teacherUserIds, visibility };
}
